/** The minimum query length before a remote directory search is worth running. */
export const PEOPLE_SEARCH_MIN_LENGTH = 2;
/** Upper bound on a directory query, matching the server's own validation. */
export const PEOPLE_SEARCH_MAX_LENGTH = 100;
/**
 * How long a typed query must sit still before it reaches the directory. One
 * value for every person field and access rail (d9): each person's directory
 * allowance is 60 searches a minute per bucket, and a request per keystroke
 * spends it on queries nobody finished typing.
 */
export const PEOPLE_SEARCH_DEBOUNCE_MS = 250;
/**
 * Whether a typed query should reach the directory at all. Lives here, beside
 * the contract, so the data-blind `PeopleField` and the api-clients adapters
 * apply the same bound (`@playbookai/ui` may not import api-clients).
 */
export function isSearchableQuery(query) {
    // Validate the same Unicode representation the server receives. Without
    // normalization, compatibility characters can pass this client-side bound
    // and expand past it when the request is normalized downstream.
    const trimmed = query.normalize("NFKC").trim();
    return (trimmed.length >= PEOPLE_SEARCH_MIN_LENGTH &&
        trimmed.length <= PEOPLE_SEARCH_MAX_LENGTH);
}
/**
 * Whether to fetch the next page immediately because this one came back empty.
 *
 * A page is filtered SERVER-side — existing members removed, or a membership
 * filter applied — so it can legitimately be empty while more pages remain.
 * Stopping there would show "no results" over a directory that has them, which
 * is indistinguishable from a genuinely empty search and is why this rule keeps
 * being rediscovered. The cursor, not the item count, decides whether the
 * search is finished.
 *
 * Guarded on `isFetchingNextPage` so the caller's effect cannot start a second
 * fetch while the first is in flight.
 */
export function shouldAdvancePastEmptyPage(input) {
    return (input.isSuccess &&
        !input.isFetchingNextPage &&
        input.lastPage !== undefined &&
        input.lastPage.items.length === 0 &&
        input.lastPage.nextCursor !== null);
}
/** The tag a canvas role reads as. One spelling for every module. */
export function workspaceAccessTag(access) {
    if (access.role === "owner")
        return { label: "Owner", tone: "owner" };
    if (access.isFacilitator) {
        return { label: "Facilitator", tone: "facilitator" };
    }
    return {
        label: access.role === "collaborator" ? "Collaborator" : "Viewer",
        tone: "neutral",
    };
}
function normalizeForMatch(value) {
    return value.normalize("NFKC").trim().toLocaleLowerCase("en-US");
}
/**
 * Whether a person matches a typed query, by the same rule the tenant search
 * SQL applies (`searchTenantDirectory*.sql`): the start of the whole name or of
 * any word of it, the start of the e-mail address, or the start of any part of
 * the e-mail local part split on `.`, `_` and `-`. A match never starts inside a
 * word. Used to filter the in-memory roster so it agrees with the server.
 */
export function matchesPersonQuery(person, query) {
    const q = normalizeForMatch(query);
    if (!q)
        return true;
    const name = normalizeForMatch(person.name).replace(/\s+/g, " ");
    if (` ${name}`.includes(` ${q}`))
        return true;
    const email = person.email ? normalizeForMatch(person.email) : "";
    if (!email)
        return false;
    if (email.startsWith(q))
        return true;
    const local = (email.split("@")[0] ?? "").replace(/[._-]/g, " ");
    return ` ${local}`.includes(` ${q}`);
}
/**
 * Where the query matched in `name`, for highlighting: the first word start
 * (or the whole-name start) the query begins. `null` when the name did not
 * match (an e-mail match highlights nothing).
 */
export function personNameMatchRange(name, query) {
    const q = normalizeForMatch(query);
    if (!q)
        return null;
    // Offsets index the name as shown. When NFKC would change it (full-width
    // letters, ligatures), the matcher's normalized form and these offsets no
    // longer line up, so no highlight is safer than a wrong one.
    if (name.normalize("NFKC") !== name)
        return null;
    const lower = name.toLocaleLowerCase("en-US");
    for (let i = 0; i < name.length; i += 1) {
        const atWordStart = i === 0 ? !/\s/.test(name[0]) : /\s/.test(name[i - 1]);
        if (atWordStart && lower.startsWith(q, i)) {
            return { start: i, end: i + q.length };
        }
    }
    return null;
}
/**
 * One table of cases for the any-word rule, run against BOTH this matcher (unit
 * tests) and the auth search SQL (auth integration test), so the in-memory
 * roster and the server can never disagree about who matches.
 */
export const PEOPLE_MATCH_CASES = Object.freeze([
    {
        name: "Rohan Mehta",
        email: "rm@example.invalid",
        query: "ro",
        matches: true,
    },
    {
        name: "Marco Romano",
        email: "mr@example.invalid",
        query: "ro",
        matches: true,
    },
    {
        name: "Barrow Smith",
        email: "bs@example.invalid",
        query: "ro",
        matches: false,
    },
    {
        name: "Rohan Mehta",
        email: "rm@example.invalid",
        query: "rohan me",
        matches: true,
    },
    {
        name: "Nothing Alike",
        email: "rohan.mehta@example.invalid",
        query: "mehta",
        matches: true,
    },
    {
        name: "Other Person",
        email: "first_second-third@example.invalid",
        query: "third",
        matches: true,
    },
    {
        name: "Other Person",
        email: "first_second-third@example.invalid",
        query: "example",
        matches: false,
    },
    {
        name: "Grace Liu",
        email: "gl@example.invalid",
        query: "LIU",
        matches: true,
    },
    {
        name: "Grace Liu",
        email: "gl@example.invalid",
        query: "race",
        matches: false,
    },
]);
//# sourceMappingURL=people-source.js.map